import { useCallback, useEffect, useState } from "react"
import axios from "axios"
import {
  getActiveDocuments,
  type DocumentSummary,
} from "modules/permit"
import type { UploadedFileItem } from "modules/permit/components/RequiredDocumentsSection"
import type { TemplateConfiguration } from "shared/api/templateService"
import { useRequiredDocuments, type RequiredDocumentItem } from "./useRequiredDocuments"

interface UsePermitDocumentsParams {
  tenantId: string
  templateConfig: TemplateConfiguration | null
  selectedActivities: string[]
  uploadedFiles: UploadedFileItem[]
}

interface UsePermitDocumentsResult {
  availableDocuments: DocumentSummary[]
  requiredDocuments: RequiredDocumentItem[]
  isDocumentsLoading: boolean
  documentsError: string | null
  reloadDocuments: () => void
}

export const usePermitDocuments = ({
  tenantId,
  templateConfig,
  selectedActivities,
  uploadedFiles,
}: UsePermitDocumentsParams): UsePermitDocumentsResult => {
  const [availableDocuments, setAvailableDocuments] = useState<DocumentSummary[]>([])
  const [isDocumentsLoading, setIsDocumentsLoading] = useState(false)
  const [documentsError, setDocumentsError] = useState<string | null>(null)
  const [reloadToken, setReloadToken] = useState(0)

  const reloadDocuments = useCallback(() => {
    setReloadToken((token) => token + 1)
  }, [])

  useEffect(() => {
    if (templateConfig?.attachmentEnabled === false) {
      setAvailableDocuments([])
      setDocumentsError(null)
      setIsDocumentsLoading(false)
      return
    }

    let cancelled = false
    setIsDocumentsLoading(true)
    setDocumentsError(null)

    getActiveDocuments(tenantId)
      .then((documents) => {
        if (cancelled) return
        setAvailableDocuments(documents)
      })
      .catch((error) => {
        if (cancelled) return
        console.error("Failed to fetch documents", error)
        const message = axios.isAxiosError(error)
          ? (error.response?.data as any)?.message ??
            error.response?.statusText ??
            error.message ??
            "ไม่สามารถโหลดรายการเอกสารที่ต้องแนบได้"
          : "ไม่สามารถโหลดรายการเอกสารที่ต้องแนบได้"
        setDocumentsError(message)
        setAvailableDocuments([])
      })
      .finally(() => {
        if (!cancelled) {
          setIsDocumentsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [tenantId, reloadToken, templateConfig?.attachmentEnabled])

  const requiredDocuments = useRequiredDocuments({
    templateConfig,
    selectedActivities,
    availableDocuments,
    uploadedFiles,
  })

  return {
    availableDocuments,
    requiredDocuments,
    isDocumentsLoading,
    documentsError,
    reloadDocuments,
  }
}

